/**
 * Treść strony /zamowienie-specjalne — wydzielona z pages/zamowienie-specjalne.tsx
 * jako osobny, JSON-serializable config. Logika formularza krokowego
 * (walidacja, upload zdjęcia, wysyłka, stan `SpecialOrderPayload`) zostaje
 * w kodzie — tu wydzielona jest treść redakcyjna, w tym etykiety/opcje/
 * placeholdery pól (ten sam poziom szczegółowości co warsztatyContent.ts).
 */
export interface CategoryCard {
  iconKey: string;
  label: string;
  desc: string;
}

export interface ProcessStep {
  n: string;
  title: string;
  note: string;
}

export interface FaqEntry {
  q: string;
  a: string;
}

export const ZAMOWIENIE_SPECJALNE_CONTENT = {
  formSteps: ['Pomysł', 'Szczegóły', 'Termin', 'Kontakt'] as string[],
  orderTypes: [
    'Bukiet', 'Flowerbox', 'Kompozycja kwiatowa',
    'Kwiaty doniczkowe', 'Konkretne kwiaty',
    'Kwiaty na wydarzenie', 'Kwiaty + dodatki', 'Inne',
  ] as string[],
  hero: {
    kicker: 'Zamówienia specjalne · Lublin',
    tagline: 'Nie znalazłeś tego, czego szukasz?',
    headingLine1: 'Kwiaty na zamówienie',
    headingLine2: 'w Lublinie',
    headingAccent: '- opisz swój pomysł',
    body: 'Nie znalazłeś odpowiednich kwiatów, kompozycji albo konkretnego produktu? Opowiedz nam, czego szukasz - postaramy się przygotować lub sprowadzić coś specjalnie dla Ciebie.',
    ctaOrderLabel: 'Wyślij zapytanie o kwiaty',
    ctaHowLabel: 'Jak to działa?',
  },
  categories: {
    eyebrow: 'Możliwości',
    heading: 'Opisz, jakich kwiatów potrzebujesz',
    ctaLabel: 'Zapytaj →',
    items: [
      { iconKey: 'flower2', label: 'Kwiaty', desc: 'Szukasz konkretnego gatunku, koloru lub odmiany?' },
      { iconKey: 'heart', label: 'Bukiety', desc: 'Masz własny pomysł na bukiet albo potrzebujesz czegoś nietypowego?' },
      { iconKey: 'leaf', label: 'Doniczkowe', desc: 'Szukasz konkretnej rośliny do domu, biura lub na prezent?' },
      { iconKey: 'layers', label: 'Kompozycje', desc: 'Potrzebujesz wyjątkowej kompozycji kwiatowej?' },
      { iconKey: 'gift', label: 'Kwiaty i dodatki', desc: 'Chcesz połączyć kwiaty z prezentem, bilecikiem lub innym dodatkiem?' },
      { iconKey: 'sparkles', label: 'Inne', desc: 'Masz pomysł, którego nie ma powyżej? Napisz nam o nim.' },
    ] satisfies CategoryCard[],
  },
  process: {
    eyebrow: 'Proces',
    heading: 'Jak to działa?',
    ctaLabel: 'Wyślij zapytanie o kwiaty',
    steps: [
      { n: '01', title: 'Opowiedz nam, czego szukasz', note: 'Opisz pomysł, gatunek, kolor, okazję lub wyślij zdjęcie inspiracji.' },
      { n: '02', title: 'Sprawdzimy dostępność i możliwości realizacji', note: 'Postaramy się znaleźć lub przygotować to, czego szukasz - jeżeli będzie to możliwe.' },
      { n: '03', title: 'Skontaktujemy się z Tobą i przedstawimy szczegóły', note: 'Potwierdzimy możliwość realizacji, termin i cenę przed przystąpieniem do pracy.' },
      { n: '04', title: 'Po akceptacji przygotujemy Twoje zamówienie', note: 'Dopiero po Twojej zgodzie rozpoczynamy realizację.' },
    ] satisfies ProcessStep[],
  },
  formIntro: {
    eyebrow: 'Formularz',
    heading: 'Masz konkretny pomysł?',
    body: 'Możesz opisać nam dokładnie to, czego potrzebujesz. Jeśli masz zdjęcie inspiracji, możesz również je przesłać.',
  },
  reassurance: {
    eyebrow: 'Bez obaw',
    headingLine1: 'Nie jesteś pewien,',
    headingAccent: 'czy możemy to zorganizować?',
    body: 'Napisz do nas nawet wtedy, jeśli nie wiesz dokładnie, czego szukasz. Możesz opisać okazję, budżet, kolorystykę albo po prostu powiedzieć nam, jaki efekt chcesz osiągnąć. Wspólnie poszukamy najlepszego rozwiązania.',
    ctaLabel: 'Zapytaj Bukietową',
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Najczęstsze pytania',
    items: [
      { q: 'Czy mogę zamówić kwiat, którego nie ma w sklepie?', a: 'Tak. Właśnie po to istnieje formularz zamówień specjalnych - możesz poprosić nas o konkretny kwiat, roślinę lub produkt, który aktualnie nie jest w naszej regularnej ofercie. Sprawdzimy dostępność i poinformujemy Cię o możliwościach realizacji.' },
      { q: 'Czy mogę zamówić konkretny gatunek kwiatów?', a: 'Tak. Jeśli zależy Ci na konkretnym gatunku - np. piwoniach, anemonach, fritillariach lub rzadszych odmianach - opisz nam to w formularzu. Postaramy się to zorganizować, jeżeli będzie to możliwe.' },
      { q: 'Czy mogę wysłać zdjęcie inspiracji?', a: 'Tak. W formularzu zamówienia specjalnego możesz dołączyć zdjęcie w formacie JPG, PNG lub WEBP. Zdjęcie pomoże nam lepiej zrozumieć Twój pomysł i zaproponować odpowiednie rozwiązanie.' },
      { q: 'Czy mogę zamówić konkretną roślinę doniczkową?', a: 'Tak. Jeśli szukasz konkretnej rośliny - monstery, palmy, fikusa czy innej - opisz nam dokładnie, czego potrzebujesz (wielkość, odmiana, przeznaczenie). Sprawdzimy możliwości.' },
      { q: 'Czy mogę zamówić bukiet według własnego pomysłu?', a: 'Oczywiście. Możesz opisać swój pomysł w formularzu - kwiaty, kolory, styl, rozmiar - lub dołączyć zdjęcie inspiracji. Nasi floryści postarają się jak najlepiej odtworzyć Twoją wizję.' },
      { q: 'Czy można przygotować większą kompozycję?', a: 'Tak. Przyjmujemy zamówienia na większe kompozycje - dekoracje na wydarzenia, instalacje florystyczne, zestawy na uroczyste okazje. Opisz nam potrzeby, a my podamy możliwości i wycenę.' },
      { q: 'Czy mogę podać orientacyjny budżet?', a: 'Tak i to jest bardzo pomocne. Znając Twój budżet, możemy dobrać kwiaty i kompozycję tak, aby jak najlepiej go wykorzystać i nie proponować rozwiązań poza Twoimi możliwościami.' },
      { q: 'Jak wcześnie należy złożyć zamówienie specjalne?', a: 'Im wcześniej, tym lepiej. Niestandardowe zamówienia - szczególnie te wymagające sprowadzenia konkretnych kwiatów - wymagają więcej czasu na realizację. Prosimy składać je z odpowiednim wyprzedzeniem.' },
      { q: 'Czy każde zamówienie specjalne może zostać zrealizowane?', a: 'Nie możemy tego zagwarantować. Staramy się realizować każde zapytanie, ale dostępność niektórych kwiatów, roślin lub produktów może być ograniczona sezonowo lub logistycznie. Zawsze poinformujemy Cię o możliwościach i alternatywach.' },
      { q: 'Czy przed realizacją poznam cenę?', a: 'Tak. Przed przystąpieniem do realizacji skontaktujemy się z Tobą i przedstawimy szczegóły - w tym cenę. Zamówienie specjalne to najpierw zapytanie, niezobowiązujące do zakupu.' },
    ] satisfies FaqEntry[],
  },
  form: {
    step1Heading: 'Opowiedz nam, czego szukasz',
    descriptionLabel: 'Opisz, czego potrzebujesz',
    descriptionPlaceholder: 'Np. szukam dużej rośliny doniczkowej do biura, bukietu z białych róż albo kompozycji w określonych kolorach…',
    orderTypeLabel: 'Rodzaj zamówienia',
    orderTypePlaceholder: 'Wybierz rodzaj zamówienia',
    optionalSuffix: '(opcjonalne)',
    inspirationImageLabel: 'Zdjęcie inspiracji',
    inspirationImageNote: 'Nie masz zdjęcia? Nic nie szkodzi - po prostu opisz nam swój pomysł powyżej.',
    uploadRemoveAriaLabel: 'Usuń zdjęcie',
    uploadPreviewAlt: 'Inspiracja',
    uploadTitle: 'Dodaj zdjęcie inspiracji',
    uploadHint: 'Przeciągnij plik lub',
    uploadHintLink: 'kliknij, aby wybrać',
    uploadHintFormats: '· JPG, PNG, WEBP',

    step2Heading: 'Jak ma wyglądać?',
    step2Body: 'Wszystkie pola są opcjonalne - wypełnij tylko te, które są dla Ciebie ważne.',
    preferredFlowersLabel: 'Preferowane kwiaty / rośliny',
    preferredFlowersPlaceholder: 'Np. białe róże, tulipany, monstera',
    preferredColorsLabel: 'Preferowane kolory',
    preferredColorsPlaceholder: 'Np. odcienie różu, zieleń, pastele',
    preferredStyleLabel: 'Preferowany styl',
    preferredStylePlaceholder: 'Np. minimalistyczny, romantyczny, rustykalny',
    budgetLabel: 'Orientacyjny budżet',
    budgetPlaceholder: 'Np. 100–200 zł',
    quantityLabel: 'Liczba sztuk / wielkość zamówienia',
    quantityPlaceholder: 'Np. jeden bukiet, 5 kompozycji, 3 rośliny doniczkowe',

    step3Heading: 'Kiedy tego potrzebujesz?',
    neededByLabel: 'Preferowany termin realizacji',
    neededByNote: 'Im wcześniej się z nami skontaktujesz, tym większa szansa na znalezienie konkretnego produktu lub przygotowanie niestandardowej kompozycji. Nie możemy gwarantować dostępności w wybranym terminie.',
    additionalInfoLabel: 'Czy jest coś jeszcze, co powinniśmy wiedzieć?',
    additionalInfoPlaceholder: 'Okazja, szczególne życzenia, ograniczenia, inne informacje…',

    step4Heading: 'Jak możemy się z Tobą skontaktować?',
    fullNameLabel: 'Imię i nazwisko',
    fullNamePlaceholder: 'Anna Kowalska',
    phoneLabel: 'Telefon',
    phonePlaceholder: '600 000 000',
    emailLabel: 'Adres e-mail',
    emailPlaceholder: 'anna@przykład.pl',
    contactNote: 'Skontaktujemy się z Tobą, aby potwierdzić możliwość realizacji i ustalić szczegóły zamówienia. To jest zapytanie - nie zobowiązuje do zakupu.',

    successHeadingLine1: 'Dziękujemy!',
    successHeadingLine2: 'Otrzymaliśmy Twoje zapytanie.',
    successBody: 'Sprawdzimy możliwość realizacji Twojego zamówienia i skontaktujemy się z Tobą, aby ustalić szczegóły.',
    backToShopLabel: 'Wróć do sklepu',
    sendAnotherLabel: 'Wyślij kolejne zapytanie',

    backLabel: 'Wstecz',
    nextLabel: 'Dalej',
    submitLabel: 'Wyślij zapytanie',
    submitLabelSending: 'Wysyłanie…',
    genericServerErrorMessage: 'Błąd serwera',
    genericSubmitErrorMessage: 'Nie udało się wysłać zapytania. Spróbuj ponownie.',
  },
};
